import type { KFNode, KFEdge, CanvasShape } from "./useCanvasStore";

/**
 * Per-forge localStorage persistence for the canvas.
 *
 * Previously this was a single key holding whichever forge was last touched —
 * a crash backup rather than a cache, and two tabs on different forges
 * overwrote each other on every keystroke. Keys are now scoped per forge and
 * tracked by a most-recently-used index so the store can restore any of the
 * last few forges, and tabs on different forges never touch the same key.
 *
 * Every entry carries `revision`/`syncedRevision`. That pair is what tells the
 * store whether a stored canvas holds edits the server never received — the
 * only case in which local data may outrank the server's copy.
 */

export const STORAGE_PREFIX = "taitora-canvas-v1";
/** Double underscore so a forge id can never collide with the index. */
export const INDEX_KEY = `${STORAGE_PREFIX}:__index`;
export const STORAGE_LIMIT = 5;

/** Pre-rebrand key, plus the old single-slot key this module replaces. */
const LEGACY_KEYS = [STORAGE_PREFIX, "kenforge-canvas-v1"];

export interface PersistedCanvas {
  forgeId: string;
  rfNodes: KFNode[];
  rfEdges: KFEdge[];
  shapes: CanvasShape[];
  revealSequence: string[];
  revision: number;
  syncedRevision: number;
  /** Forge revision this canvas was last in sync with, so a restored tab states
   *  a base the server recognises instead of saving blind. Optional: entries
   *  written before this field existed are still readable. */
  serverRevision?: number;
}

export interface StoredCanvasInfo {
  forgeId: string;
  bytes: number;
}

export function canvasKey(forgeId: string): string {
  return `${STORAGE_PREFIX}:${forgeId}`;
}

function readIndex(): string[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/**
 * localStorage quota is counted in UTF-16 code units, so two bytes per
 * character, and the key counts against it as well as the value.
 */
function entryBytes(key: string, value: string): number {
  return (key.length + value.length) * 2;
}

export function readCanvas(forgeId: string): PersistedCanvas | null {
  try {
    const raw = localStorage.getItem(canvasKey(forgeId));
    if (!raw) return null;
    return JSON.parse(raw) as PersistedCanvas;
  } catch {
    return null; // corrupt entry — treat as absent rather than breaking the canvas
  }
}

export function writeCanvas(canvas: PersistedCanvas): void {
  if (!canvas.forgeId) return;
  try {
    localStorage.setItem(canvasKey(canvas.forgeId), JSON.stringify(canvas));

    // Most-recently-used first; re-writing an existing forge refreshes it
    // rather than adding a duplicate.
    const next = [canvas.forgeId, ...readIndex().filter((id) => id !== canvas.forgeId)];
    for (const evicted of next.slice(STORAGE_LIMIT)) {
      localStorage.removeItem(canvasKey(evicted));
    }
    localStorage.setItem(INDEX_KEY, JSON.stringify(next.slice(0, STORAGE_LIMIT)));
  } catch {
    // Quota exhausted or storage disabled (private mode). The canvas still
    // lives in memory and autosaves to the server; losing the local mirror is
    // not worth breaking an edit over.
  }
}

/** The forge to restore on reload — most recent entry that still has a payload. */
export function lastActiveForgeId(): string | null {
  for (const forgeId of readIndex()) {
    if (localStorage.getItem(canvasKey(forgeId)) !== null) return forgeId;
  }
  return null;
}

/** Stored forges with their size, newest first. Backs the storage usage page. */
export function listStoredCanvases(): StoredCanvasInfo[] {
  const out: StoredCanvasInfo[] = [];
  for (const forgeId of readIndex()) {
    const key = canvasKey(forgeId);
    const raw = localStorage.getItem(key);
    if (raw === null) continue; // index entry outlived its payload
    out.push({ forgeId, bytes: entryBytes(key, raw) });
  }
  return out;
}

/**
 * One-time move off the old single-slot format. The legacy blob already
 * carried its `forgeId`, so it can be re-filed under the right key; a blob
 * without one is dropped rather than guessed at.
 */
export function migrateLegacyCanvas(): void {
  for (const legacyKey of LEGACY_KEYS) {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(legacyKey);
      if (raw === null) continue;
      // Remove first, so a malformed blob cannot make this retry every reload.
      localStorage.removeItem(legacyKey);
    } catch {
      continue;
    }

    try {
      const saved = JSON.parse(raw) as Partial<PersistedCanvas>;
      if (typeof saved.forgeId !== "string" || !saved.forgeId) continue;
      writeCanvas({
        forgeId:        saved.forgeId,
        rfNodes:        saved.rfNodes        ?? [],
        rfEdges:        saved.rfEdges        ?? [],
        shapes:         saved.shapes         ?? [],
        revealSequence: saved.revealSequence ?? [],
        revision:       saved.revision       ?? 0,
        syncedRevision: saved.syncedRevision ?? 0,
      });
    } catch {
      // corrupt legacy blob — already removed above
    }
  }
}
